/**
 * NeoCoreCredencialesAppPage
 * ─────────────────────────────────────────────────────────────────
 * Gestión de credenciales del app móvil de los asociados. Centraliza
 * las 4 acciones (invite, reset, disable, unlink) en una pantalla
 * propia accesible desde el sidebar — no escondida en la ficha de
 * cada asociado.
 *
 * UX:
 *   - Tabla con búsqueda fuzzy (nombre, DPI, neo_id, teléfono).
 *   - Badge de estado del app por fila (no invitado, activo, bloqueado).
 *   - Click en "Gestionar" abre el drawer con CredencialesAppPanel.
 *
 * Pensado como paso de producción para Nechy y el NeoCore. Reusable
 * cuando crezca el padrón.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Fingerprint,
  MagnifyingGlass as Search,
  X,
  CaretRight,
  ArrowLeft,
  CheckCircle,
  WarningCircle,
  Prohibit,
  UserPlus,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { CredencialesAppPanel, type AppStatus } from '@/components/CredencialesAppPanel'

type Row = {
  id: string
  neo_id: string
  no_asociado: string
  dpi: string
  nombre_completo: string
  estado_asociado: string
  telefono_e164: string | null
  auth_user_id: string | null
  last_sign_in_at: string | null
  must_change_pw: boolean
}

function computeAppStatus(r: Pick<Row, 'auth_user_id'>): AppStatus {
  if (!r.auth_user_id) return 'no_invitado'
  return 'activo'
}

const STATUS_META: Record<AppStatus, { label: string; tone: string; Icon: PhosphorIcon }> = {
  no_invitado:      { label: 'Sin invitar',      tone: 'neutral', Icon: UserPlus },
  invitado:         { label: 'Invitado',         tone: 'warn',    Icon: WarningCircle },
  invite_expirado:  { label: 'Invitación venció', tone: 'warn',   Icon: WarningCircle },
  activo:           { label: 'Activo',           tone: 'ok',      Icon: CheckCircle },
  bloqueado:        { label: 'Bloqueado',        tone: 'err',     Icon: Prohibit },
  desconocido:      { label: '—',                tone: 'neutral', Icon: WarningCircle },
}

function StatusBadge({ status }: { status: AppStatus }) {
  const meta = STATUS_META[status]
  return (
    <span className={`creds-badge tone-${meta.tone}`}>
      <meta.Icon size={12} weight="bold" /> {meta.label}
    </span>
  )
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60_000)
  if (m < 1) return 'ahora'
  if (m < 60) return `hace ${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.floor(h / 24)
  if (d < 30) return `hace ${d} d`
  const mo = Math.floor(d / 30)
  return `hace ${mo} m`
}

export function NeoCoreCredencialesAppPage() {
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<AppStatus | 'all'>('all')
  const [selected, setSelected] = useState<Row | null>(null)

  useEffect(() => {
    void cargar()
  }, [])

  const cargar = async () => {
    setLoading(true)
    // Lista plana — para producción + 50 asociados conviene paginar, pero
    // por ahora todo el padrón en memoria. El motor de búsqueda local es
    // suficiente mientras el padrón sea < 5k filas.
    const { data, error } = await supabase
      .from('v_asociado_credenciales')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(2000)

    if (error || !data) {
      // Fallback: query directa a asociado si la vista aún no existe
      const fb = await supabase
        .from('asociado')
        .select('id, neo_id, no_asociado, dpi, nombre_completo, estado_asociado, telefono_e164, auth_user_id, updated_at')
        .order('updated_at', { ascending: false })
        .limit(2000)
      if (fb.data) {
        setRows(
          fb.data.map((r) => ({
            id: r.id,
            neo_id: r.neo_id,
            no_asociado: r.no_asociado,
            dpi: r.dpi,
            nombre_completo: r.nombre_completo,
            estado_asociado: r.estado_asociado,
            telefono_e164: r.telefono_e164,
            auth_user_id: r.auth_user_id,
            last_sign_in_at: null,
            must_change_pw: false,
          })),
        )
      }
    } else {
      setRows(data as Row[])
    }
    setLoading(false)
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rows.filter((r) => {
      const st = computeAppStatus(r)
      if (statusFilter !== 'all' && st !== statusFilter) return false
      if (!q) return true
      return (
        r.nombre_completo.toLowerCase().includes(q) ||
        r.dpi.includes(q) ||
        r.neo_id.includes(q) ||
        (r.telefono_e164 ?? '').includes(q)
      )
    })
  }, [rows, query, statusFilter])

  const counts = useMemo(() => {
    const c = { activo: 0, no_invitado: 0, bloqueado: 0 }
    for (const r of rows) {
      const st = computeAppStatus(r)
      if (st === 'activo') c.activo++
      else if (st === 'no_invitado') c.no_invitado++
      else if (st === 'bloqueado') c.bloqueado++
    }
    return c
  }, [rows])

  return (
    <div className="creds-page">
      <header className="creds-header">
        <div className="creds-title-block">
          <div className="creds-title-icon"><Fingerprint size={22} weight="duotone" /></div>
          <div>
            <h1>Credenciales del app móvil</h1>
            <p>Generá, regenerá o bloqueá el acceso de los asociados a la app móvil de Neocoop.</p>
          </div>
        </div>
        <div className="creds-stats">
          <div className="creds-stat">
            <span className="creds-stat-label">Activos</span>
            <span className="creds-stat-num" style={{ color: 'var(--success)' }}>{counts.activo}</span>
          </div>
          <div className="creds-stat">
            <span className="creds-stat-label">Sin invitar</span>
            <span className="creds-stat-num">{counts.no_invitado}</span>
          </div>
          <div className="creds-stat">
            <span className="creds-stat-label">Bloqueados</span>
            <span className="creds-stat-num" style={{ color: 'var(--error)' }}>{counts.bloqueado}</span>
          </div>
        </div>
      </header>

      <div className="creds-filters">
        <div className="creds-search">
          <Search size={14} weight="bold" />
          <input
            type="text"
            placeholder="Buscá por nombre, DPI, Neo ID o teléfono…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button onClick={() => setQuery('')} className="creds-search-clear" title="Limpiar">
              <X size={12} />
            </button>
          )}
        </div>
        <div className="creds-filter-tabs">
          {([
            ['all', 'Todos', rows.length],
            ['activo', 'Activos', counts.activo],
            ['no_invitado', 'Sin invitar', counts.no_invitado],
            ['bloqueado', 'Bloqueados', counts.bloqueado],
          ] as Array<[AppStatus | 'all', string, number]>).map(([key, label, n]) => (
            <button
              key={key}
              className={'creds-filter-tab' + (statusFilter === key ? ' active' : '')}
              onClick={() => setStatusFilter(key)}
            >
              {label} <span className="creds-filter-tab-count">{n}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="creds-loading">Cargando padrón…</div>
      ) : filtered.length === 0 ? (
        <div className="creds-empty">
          <Fingerprint size={40} weight="duotone" />
          <p>Sin asociados que coincidan con la búsqueda.</p>
        </div>
      ) : (
        <div className="creds-table-wrap">
          <table className="creds-table">
            <thead>
              <tr>
                <th>Asociado</th>
                <th>Neo ID</th>
                <th>Teléfono</th>
                <th>Estado app</th>
                <th>Última conexión</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 100).map((r) => {
                const st = computeAppStatus(r)
                return (
                  <tr key={r.id} onClick={() => setSelected(r)}>
                    <td>
                      <div className="creds-name">{r.nombre_completo}</div>
                      <div className="creds-dpi">DPI {r.dpi}</div>
                    </td>
                    <td><code className="creds-mono">{r.neo_id}</code></td>
                    <td>{r.telefono_e164 ?? <span className="creds-muted">—</span>}</td>
                    <td><StatusBadge status={st} /></td>
                    <td>{timeAgo(r.last_sign_in_at)}</td>
                    <td>
                      <button className="creds-row-cta" onClick={(e) => { e.stopPropagation(); setSelected(r) }}>
                        Gestionar <CaretRight size={12} weight="bold" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {filtered.length > 100 && (
            <div className="creds-table-foot">
              Mostrando 100 de {filtered.length}. Afiná la búsqueda para ver más.
            </div>
          )}
        </div>
      )}

      {/* Drawer lateral con el panel de gestión */}
      {selected && (
        <div className="creds-drawer-overlay" onClick={() => setSelected(null)}>
          <div className="creds-drawer" onClick={(e) => e.stopPropagation()}>
            <header className="creds-drawer-head">
              <button className="creds-drawer-back" onClick={() => setSelected(null)}>
                <ArrowLeft size={18} />
              </button>
              <div>
                <h2>{selected.nombre_completo}</h2>
                <p>Neo ID <code>{selected.neo_id}</code> · DPI {selected.dpi}</p>
              </div>
              <Link to={`/neocore/asociados/${selected.id}`} className="creds-drawer-ficha">
                Ver ficha completa <CaretRight size={12} weight="bold" />
              </Link>
            </header>
            <div className="creds-drawer-body">
              <CredencialesAppPanel
                asociadoId={selected.id}
                noAsociado={selected.no_asociado}
                nombreCompleto={selected.nombre_completo}
                appStatus={computeAppStatus(selected)}
                telefonoPreferido={selected.telefono_e164}
                inviteSentAt={null}
                onActionComplete={() => { void cargar() }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
